<template>
  <Hero />
  <main class="home-flow">
    <DeferredMount min-height="480px" @activate="loadProducts('fem')">
      <section class="collection-section" aria-labelledby="fem-title">
        <header class="section-heading">
          <div><p class="section-kicker">Fotoperiódicas / 01</p><h2 id="fem-title"> Nuestra selección </h2></div>
          <router-link to="/productos?category=feminizadas" class="section-link">Ver todas <MinimalIcon name="arrowUpRight" :size="13" /></router-link>
        </header>
        <ProductGridSkeleton v-if="loading.fem" :count="4" />
        <RotatingGrid v-else-if="products.fem.length" :items="products.fem" :size="4" :interval="4000" :cols="4" :mobile-cols="2">
          <template #default="{ items }">
            <ProductCard v-for="product in items" :key="product.id" :product="product" />
          </template>
        </RotatingGrid>
        <p v-else class="section-status">{{ errors.fem || 'Próximamente nuevas genéticas.' }} <button v-if="errors.fem" @click="loadProducts('fem')">Reintentar</button></p>
      </section>
    </DeferredMount>
    <DeferredMount min-height="480px">
      <section class="home-gallery" aria-labelledby="gallery-title">
        <header class="section-heading"><div><p class="section-kicker">Nuestra galería / 02</p><h2 id="gallery-title"> Desde el cultivo</h2></div><router-link to="/galeria" class="section-link">Explorar galería <MinimalIcon name="arrowUpRight" :size="13" /></router-link></header>
        <GalleryViewer :limit-images="5" :show-overlays="false" bento theme="light" scope="gallery" />
        <!-- CTA al pie de la galería -->
        <div class="gallery-cta">
          <p class="gallery-cta-text">¿Querés ver más contenido?</p>
          <a
            class="gallery-cta-link"
            :href="siteSettings.instagram_url"
            target="_blank"
            rel="noopener noreferrer"
          >
            <MinimalIcon name="instagram" :size="16" />
            Seguinos en Instagram
            <MinimalIcon name="arrow" :size="15" />
          </a>
        </div>
      </section>
    </DeferredMount>
    <DeferredMount min-height="480px" @activate="loadProducts('auto')">
      <section class="collection-section" aria-labelledby="auto-title">
        <header class="section-heading"><div><p class="section-kicker">Automáticas / 03</p><h2 id="auto-title"> Nuestra selección</h2></div><router-link to="/productos?category=automaticas" class="section-link">Ver todas <MinimalIcon name="arrowUpRight" :size="13" /></router-link></header>
        <ProductGridSkeleton v-if="loading.auto" :count="4" />
        <RotatingGrid v-else-if="products.auto.length" :items="products.auto" :size="4" :interval="4000" :cols="4" :mobile-cols="2">
          <template #default="{ items }">
            <ProductCard v-for="product in items" :key="product.id" :product="product" />
          </template>
        </RotatingGrid>
        <p v-else class="section-status">{{ errors.auto || 'Próximamente nuevas genéticas.' }} <button v-if="errors.auto" @click="loadProducts('auto')">Reintentar</button></p>
      </section>
    </DeferredMount>
    <DeferredMount min-height="850px">
      <AwardWinningGenetics />
    </DeferredMount>
    <DeferredMount min-height="480px" @activate="loadProducts('merch')">
      <section class="home-merch" aria-labelledby="merch-title">
        <header class="section-heading"><div><p class="section-kicker">Merch / 05</p><h2 id="merch-title">Llevá Heaven’s Fruit con vos</h2></div><router-link to="/productos?category=merch" class="section-link">Ver todo <MinimalIcon name="arrowUpRight" :size="13" /></router-link></header>
        <ProductGridSkeleton v-if="loading.merch" :count="4" />
        <RotatingGrid v-else-if="products.merch.length" :items="products.merch" :size="4" :interval="4000" :cols="4" :mobile-cols="2">
          <template #default="{ items }">
            <ProductCard v-for="product in items" :key="product.id" :product="product" tone="light" />
          </template>
        </RotatingGrid>
        <p v-else class="section-status">{{ errors.merch || 'Próximamente nuevos productos merch.' }} <button v-if="errors.merch" @click="loadProducts('merch')">Reintentar</button></p>
      </section>
    </DeferredMount>
    <section id="puntos-de-venta" class="home-stores" aria-labelledby="stores-title">
      <header class="section-heading"><div><p class="section-kicker">Puntos de venta / 06</p><h2 id="stores-title">Encontranos cerca</h2></div></header>
      <SalesPointsMap layout="home" />
    </section>
  </main>
</template>

<script>
export default { name: 'PageHome' }
</script>

<script setup>
import { reactive, defineAsyncComponent, watch, nextTick, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import Hero from '@/components/Hero.vue'
import ProductCard from '@/components/ProductCard.vue'
import DeferredMount from '@/components/common/DeferredMount.vue'
import RotatingGrid from '@/components/common/RotatingGrid.vue'
import MinimalIcon from '@/components/common/MinimalIcon.vue'
import ProductGridSkeleton from '@/components/common/ProductGridSkeleton.vue'
import { getProductsPaginated } from '@/services/products'
import { useSiteSettings, loadSiteSettings } from '@/services/settings'
const GalleryViewer = defineAsyncComponent(() => import('@/components/GalleryViewer.vue'))
const AwardWinningGenetics = defineAsyncComponent(() => import('@/components/home/AwardWinningGenetics.vue'))
const SalesPointsMap = defineAsyncComponent(() => import('@/components/SalesPointsMap.vue'))
const products = reactive({ fem: [], auto: [], merch: [] })
const loading = reactive({ fem: true, auto: true, merch: true })
const errors = reactive({ fem: '', auto: '', merch: '' })
async function loadProducts(type) {
  loading[type] = true
  errors[type] = ''
  try {
    // Se traen varios productos para que el carrusel rote por grupos de 4
    const response = await getProductsPaginated(1, 12, type === 'merch' ? { category: ['merch'] } : { type: [type] })
    products[type] = response.results || []
  } catch {
    errors[type] = type === 'merch' ? 'No pudimos cargar los productos merch.' : 'No pudimos cargar las genéticas.'
  } finally {
    loading[type] = false
  }
  }

  // Salto directo a una sección por hash (p. ej. #puntos-de-venta). La sección ya
  // está en el DOM (sin montaje diferido): un solo scroll suave hasta ella, sin
  // bajar en pasos (eso se peleaba con el scroll-behavior suave y se frenaba en
  // los carouseles).
  const route = useRoute()

  const siteSettings = useSiteSettings()

  async function scrollToHash(hash) {
    await nextTick()
    const el = document.getElementById(hash.replace('#', ''))
    if (!el) return

    // El contenido diferido que queda arriba crece al pasar por él y corre la
    // sección hacia abajo: se re-dispara el scroll hasta que quede en su lugar
    // (el scroll-margin-top la deja ~8.5rem bajo el header fijo).
    for (let attempt = 0; attempt < 8; attempt += 1) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      await new Promise((resolve) => setTimeout(resolve, 320))

      const top = el.getBoundingClientRect().top
      if (top >= 0 && top <= 260) return
    }
  }

  onMounted(() => {
    loadSiteSettings()
    if (route.hash) scrollToHash(route.hash)
  })

  watch(() => route.hash, (hash) => {
    if (hash) scrollToHash(hash)
  })
  </script>

<style scoped>
/* La página no lleva tope de ancho: cada sección se alinea sola así la galería
   puede pintar una banda a todo el ancho sin salirse del viewport. */
.home-flow { width: 100%; }
/* Mismo contenedor que el navbar: el contenido llega a los mismos extremos */
.collection-section, .home-stores {
  max-width: calc(var(--container-width, 1200px) + 2rem);
  margin-inline: auto;
  padding: clamp(3.5rem, 8vw, 7rem) 1rem;
}
/* 8.5rem: deja libre el header fijo (marquee 34px + navbar) al saltar a #puntos-de-venta */
.home-stores { scroll-margin-top: 8.5rem; }
/* Galería: banda blanca a todo el ancho. El padding centra el contenido con el
   mismo ancho que el navbar y deja la sección ocupando todo el viewport. */
.home-gallery {
  padding: clamp(3.5rem, 8vw, 7rem) max(1rem, calc((100% - var(--container-width, 1200px)) / 2));
  background: #ffffff;
}
.home-gallery .section-kicker { color: #6b6b6b; }
.home-gallery .section-heading h2 { color: #000000;  }
.home-gallery .section-link { color: #000000; border-bottom-color: rgba(0, 0, 0, 0.35); }
.home-gallery .section-link:hover { border-bottom-color: #000000; }
.home-merch {
  padding: clamp(3.5rem, 8vw, 7rem) max(1rem, calc((100% - var(--container-width, 1200px)) / 2));
  background: #fff;
  color: #000;
}
.home-merch .section-kicker, .home-merch .section-status { color: #666; }
.home-merch .section-heading h2, .home-merch .section-link { color: #000; }
.home-merch .section-link { border-bottom-color: rgba(0, 0, 0, .35); }
.home-merch .section-link:hover { border-bottom-color: #000; }
.home-merch .section-status button { color: #000; }
.home-merch :deep(.gallery-dot), .home-merch :deep(.skeleton-dot) { background: rgba(0, 0, 0, .25); }
.home-merch :deep(.gallery-dot.is-active), .home-merch :deep(.skeleton-dot.is-active) { background: rgba(0, 0, 0, .15); }
.home-merch :deep(.gallery-dot-fill) { background: #000; }
.home-merch :deep(.skeleton-body) { border-color: rgba(0, 0, 0, .15); }
.home-merch :deep(.skeleton-line) { background: #ddd; }
/* CTA de la galería: ¿querés ver más contenido? → Instagram */
.gallery-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: clamp(2rem, 4vw, 3rem);
  padding-top: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.14);
}
.gallery-cta-text { margin: 0; color: #6b6b6b; font-size: 0.7rem; letter-spacing: 0.12em; text-transform: uppercase; }
.gallery-cta-link {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  padding-bottom: 0.35rem;
  color: #000000;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.35);
  transition: gap 0.3s ease, border-color 0.3s ease;
}
.gallery-cta-link:hover { gap: 1rem; border-bottom-color: #000000; }
@media (max-width: 600px) {
  .gallery-cta { flex-direction: column; align-items: center; text-align: center; }
}
.section-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 1.5rem; margin-bottom: 2.5rem; }
.section-kicker { color: #888; font-size: .65rem; letter-spacing: .18em; text-transform: uppercase; margin: 0 0 .85rem; }
/* Títulos de sección: mayúsculas, más chicos y algo más bold */
.section-heading h2 { margin: 0; font-size: clamp(2rem, 5vw, 3rem); text-transform: uppercase ; font-weight: 700; letter-spacing: -.015em; line-height: 1.12; }
.section-link { display: inline-flex; align-items: center; gap: 1rem; flex-shrink: 0; color: #fff; font-size: .7rem; font-weight: 600; text-transform: uppercase; letter-spacing: .08em; text-decoration: none; padding: .75rem 0; border-bottom: 1px solid #ffffff50; -webkit-tap-highlight-color: transparent; }
.section-status { color: #aaa; padding: 3rem 0; }
.section-status button { color: #fff; border: 0; background: none; text-decoration: underline; }
@media (max-width: 600px) {
  .collection-section, .home-stores, .home-gallery { padding-block: 3.5rem; }
  .section-heading { align-items: center; flex-direction: column; gap: .75rem; text-align: center; }
  .section-heading > div { width: 100%; }
  .section-link { justify-content: center; }
  .section-status { text-align: center; }
}
</style>
